import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Button, 
  Card, 
  CardContent, 
  Alert,
  CircularProgress,
  Grid,
  Chip
} from '@mui/material';

const API_BASE = 'http://localhost:5000/api';

function App() {
  const [backendStatus, setBackendStatus] = useState('testing');
  const [artisans, setArtisans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    testBackend();
  }, []);

  const testBackend = async () => {
    try {
      const response = await fetch(`${API_BASE}/test`);
      const data = await response.json();
      if (data.success) {
        setBackendStatus('connected');
        setMessage('Backend connecté avec succès !');
      } else {
        setBackendStatus('error');
        setMessage('Erreur de connexion au backend');
      }
    } catch (error) {
      setBackendStatus('error');
      setMessage(`Erreur: ${error.message}`);
    }
  };

  const createDemoArtisans = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/artisans-public/demo/creer-artisans`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setMessage(`✅ ${data.message}`);
        loadPlombiers();
      } else {
        setMessage(`❌ Erreur: ${data.message}`);
      }
    } catch (error) {
      setMessage(`❌ Erreur: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadPlombiers = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/artisans-public/type/plombier`);
      const data = await response.json();
      
      if (data.success) {
        setArtisans(data.data.artisans);
        setMessage(`✅ ${data.data.artisans.length} plombiers chargés`);
      } else {
        setMessage(`❌ Erreur: ${data.message}`);
      }
    } catch (error) {
      setMessage(`❌ Erreur: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const testReservation = async () => {
    if (artisans.length === 0) {
      setMessage('❌ Créez d\'abord les artisans de démo');
      return;
    }

    setLoading(true);
    const plombier = artisans[0];
    const service = plombier.services[0];

    const reservationData = {
      client: {
        prenom: 'Jean',
        nom: 'Dupont',
        email: 'jean.dupont@email.com',
        telephone: '+33123456789',
        adresse: {
          rue: '123 rue de la Paix',
          ville: 'Paris',
          codePostal: '75001'
        }
      },
      artisanId: plombier._id,
      artisanType: plombier.typeMetier,
      service: {
        nom: service.nom,
        description: service.description,
        prix: service.prix,
        duree: service.duree
      },
      dateRendezVous: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      heureRendezVous: '14:30',
      notes: 'Test de réservation depuis React'
    };

    try {
      const response = await fetch(`${API_BASE}/reservations/creer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(reservationData)
      });
      const data = await response.json();
      
      if (data.success) {
        setMessage(`✅ Réservation créée ! ID: ${data.data.reservation._id}`);
      } else {
        setMessage(`❌ Erreur réservation: ${JSON.stringify(data)}`);
      }
    } catch (error) {
      setMessage(`❌ Erreur: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          🛠️ LYS Artisans
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Interface de test React connectée au backend
        </Typography>
      </Box>

      {/* Status Backend */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            📡 Statut du Backend
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Chip 
              label={backendStatus === 'connected' ? 'Connecté' : backendStatus === 'error' ? 'Erreur' : 'Test...'}
              color={backendStatus === 'connected' ? 'success' : backendStatus === 'error' ? 'error' : 'default'}
            />
            <Button variant="outlined" onClick={testBackend}>
              🔄 Retester
            </Button>
          </Box>
          {message && (
            <Alert severity={message.includes('✅') ? 'success' : message.includes('❌') ? 'error' : 'info'}>
              {message}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Actions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            🎯 Actions de Test
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <Button 
              variant="contained" 
              color="primary"
              onClick={createDemoArtisans}
              disabled={loading || backendStatus !== 'connected'}
            >
              {loading ? <CircularProgress size={20} /> : '👷‍♂️ Créer Artisans Démo'}
            </Button>
            <Button 
              variant="contained" 
              color="secondary"
              onClick={loadPlombiers}
              disabled={loading || backendStatus !== 'connected'}
            >
              {loading ? <CircularProgress size={20} /> : '🔧 Charger Plombiers'}
            </Button>
            <Button 
              variant="contained" 
              color="success"
              onClick={testReservation}
              disabled={loading || backendStatus !== 'connected' || artisans.length === 0}
            >
              {loading ? <CircularProgress size={20} /> : '📅 Test Réservation'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Liste des Artisans */}
      {artisans.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              🔧 Plombiers Disponibles ({artisans.length})
            </Typography>
            <Grid container spacing={2}>
              {artisans.map((artisan, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6">
                        {artisan.nomEntreprise}
                      </Typography>
                      <Typography color="text.secondary" gutterBottom>
                        {artisan.prenom} {artisan.nom}
                      </Typography>
                      <Typography variant="body2">
                        📍 {artisan.adresse.ville} ({artisan.adresse.codePostal})
                      </Typography>
                      <Typography variant="body2">
                        📞 {artisan.telephone}
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        <Chip 
                          label={`${artisan.services.length} services`} 
                          size="small" 
                          color="primary" 
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Backend API: <code>http://localhost:5000/api</code>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Frontend React: <code>http://localhost:3001</code>
        </Typography>
      </Box>
    </Container>
  );
}

export default App;